import { Injectable, effect, inject, signal } from '@angular/core';
import { AuthService } from '../auth/auth.service';

/**
 * Inactivity auto-logout with a resume prompt.
 *
 * After `IDLE_MS` without user activity, a warning modal appears and counts
 * down `WARN_S` seconds. Choosing "Lanjutkan sesi" refreshes the token and
 * resets the timer; letting it reach zero logs the user out.
 *
 * Activity before the warning simply resets the idle timer (no prompt).
 */
@Injectable({ providedIn: 'root' })
export class IdleService {
  private auth = inject(AuthService);

  private static readonly IDLE_MS = 20 * 60 * 1000; // 20 minutes
  private static readonly WARN_S = 60;              // countdown seconds

  readonly warning = signal(false);
  readonly secondsLeft = signal(0);

  private readonly events: (keyof WindowEventMap)[] =
    ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'];

  private started = false;
  private idleTimer?: ReturnType<typeof setTimeout>;
  private countdownTimer?: ReturnType<typeof setInterval>;
  private lastActivity = 0;

  constructor() {
    // Start/stop tracking with the auth state.
    effect(() => {
      if (this.auth.currentUser()) this.start();
      else this.stop();
    });
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.events.forEach(e =>
      window.addEventListener(e, this.onActivity, { passive: true }),
    );
    document.addEventListener('visibilitychange', this.onActivity);
    this.reset();
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    this.events.forEach(e => window.removeEventListener(e, this.onActivity));
    document.removeEventListener('visibilitychange', this.onActivity);
    this.clearTimers();
    this.warning.set(false);
  }

  /** User chose to keep the session alive. */
  resume(): void {
    void this.auth.refreshSession();
    this.reset();
  }

  /** User chose to end the session now, or the countdown elapsed. */
  logout(): void {
    this.reset();
    this.auth.logout();
  }

  private onActivity = (): void => {
    if (!this.started || !this.auth.currentUser()) return;
    // While the prompt is up, only the explicit buttons act — avoid
    // accidental resumes from stray movement.
    if (this.warning()) return;
    const now = Date.now();
    if (now - this.lastActivity < 1000) return; // throttle
    this.lastActivity = now;
    this.reset();
  };

  private reset(): void {
    this.clearTimers();
    this.warning.set(false);
    this.secondsLeft.set(0);
    this.idleTimer = setTimeout(() => this.showWarning(), IdleService.IDLE_MS);
  }

  private showWarning(): void {
    this.warning.set(true);
    this.secondsLeft.set(IdleService.WARN_S);
    this.countdownTimer = setInterval(() => {
      const next = this.secondsLeft() - 1;
      this.secondsLeft.set(next);
      if (next <= 0) {
        this.clearTimers();
        this.auth.logout();
      }
    }, 1000);
  }

  private clearTimers(): void {
    if (this.idleTimer) { clearTimeout(this.idleTimer); this.idleTimer = undefined; }
    if (this.countdownTimer) { clearInterval(this.countdownTimer); this.countdownTimer = undefined; }
  }
}
